import { call, put, takeEvery, all } from '@redux-saga/core/effects';
import axios from 'axios';
import ENDPOINTS from '../../api/endPoints';
import {
  postNowRequest,
  postNowSuccess,
  postNowFailure,
} from '../slices/postNowSlice';
import type { PostNowPayload, PostNowResult } from '../slices/postNowSlice';
import {
  scheduleCampaignRequest,
  scheduleCampaignSuccess,
  scheduleCampaignFailure,
  updateCampaignRequest,
  updateCampaignSuccess,
  updateCampaignFailure,
  fetchScheduledCampaignsRequest,
  fetchScheduledCampaignsSuccess,
  fetchScheduledCampaignsFailure,
} from '../slices/scheduleCampaignSlice';
import type { ScheduleCampaignRequest, UpdateCampaignRequest } from '../slices/scheduleCampaignSlice';

const baseUrl = import.meta.env.VITE_TRIGGER_URL;

// Function to post to a single platform
function* postToPlatform(
  platform: string,
  restaurantId: string,
  contentId: number,
  flyerId?: number
): Generator<any, PostNowResult, any> {
  try {
    // Handle email separately
    if (platform.toLowerCase() === 'email') {
      const endpoint = `${baseUrl}/social-media/email/${restaurantId}`;
      
      const payload: any = {
        toEmail: [],
        contentId,
        sendToAll: true,
      };

      if (flyerId) {
        payload.flyerId = flyerId;
      }

      yield call(axios.post, endpoint, payload);

      return {
        platform: 'email',
        success: true,
      };
    }
    
    // Handle other platforms
    const endpoint = `${baseUrl}/${platform.toLowerCase()}/post/${restaurantId}`;
    
    const payload: any = {
      contentId,
    };

    if (flyerId) {
      payload.flyerId = flyerId;
    }

    yield call(axios.post, endpoint, payload);

    return {
      platform,
      success: true,
    };
  } catch (error: any) {
    console.error(`Error posting to ${platform}:`, error);
    return {
      platform,
      success: false,
      error: error.message || `Failed to post to ${platform}`,
    };
  }
}

// Main saga to handle posting to multiple platforms in parallel
function* postNowSaga(action: { type: string; payload: PostNowPayload }): Generator<any, void, any> {
  try {
    const { platformContentMap, restaurantId, flyerId } = action.payload;

    // Create an array of posting tasks for each platform with its specific content_id
    const postTasks = Object.entries(platformContentMap).map(([platform, contentId]) =>
      call(postToPlatform, platform, restaurantId, contentId, flyerId)
    );

    // Execute all posts in parallel using all()
    const results: PostNowResult[] = yield all(postTasks);

    // Dispatch success with results
    yield put(postNowSuccess(results));

    // Check if all succeeded
    const allSucceeded = results.every((result) => result.success);
    if (!allSucceeded) {
      const failedPlatforms = results
        .filter((result) => !result.success)
        .map((result) => result.platform)
        .join(', ');
      console.warn(`Some posts failed: ${failedPlatforms}`);
    }
  } catch (error: any) {
    console.error('Error in postNowSaga:', error);
    yield put(postNowFailure(error.message || 'Failed to post to platforms'));
  }
}

// API call to schedule campaign for a specific platform
function* scheduleCampaignForPlatform(
  platform: string,
  contentId: number,
  startDate: string,
  endDate: string,
  restaurantId: string,
  flyerId?: number
): Generator<any, any, any> {
  const apiUrl = `${ENDPOINTS.schedule_campaign}/${restaurantId}`;

  const requestBody: any = {
    start_date: startDate,
    end_date: endDate,
    generated_content_id: contentId,
    social_media: [platform.toUpperCase()],
  };

  if (flyerId) {
    requestBody.flyer_id = flyerId;
  }

  const response: { data: any } = yield call(
    axios.post,
    apiUrl,
    requestBody
  );

  return response.data;
}

function* scheduleCampaignSaga(action: { type: string; payload: ScheduleCampaignRequest }): Generator<any, void, any> {
  try {
    const { platform_content_map, start_date, end_date, restaurant_id, flyer_id, generated_content_id, social_media, scheduled_date } = action.payload;

    // Check if new payload format is being used (with social_media and scheduled_date)
    if (social_media !== undefined && scheduled_date !== undefined) {
      // New format: make separate API calls for each platform
      try {
        // Parse the social_media JSON string to get array of platforms
        const platforms: string[] = JSON.parse(social_media);
        const results: any[] = [];

        // Make a separate API call for each platform
        for (const platform of platforms) {
          try {
            const apiUrl = `${ENDPOINTS.schedule_campaign}/${restaurant_id}`;

            const requestBody: any = {
              start_date,
              end_date,
              generated_content_id,
              social_media: JSON.stringify([platform]),  // Send only one platform at a time as array
              scheduled_date,
            };

            if (flyer_id) {
              requestBody.flyer_id = flyer_id;
            }

            const response: { data: any } = yield call(
              axios.post,
              apiUrl,
              requestBody
            );

            results.push({
              platform,
              success: true,
              data: response.data,
            });
          } catch (platformError) {
            console.error(`Error scheduling campaign for ${platform}:`, platformError);

            results.push({
              platform,
              success: false,
              error: platformError instanceof Error ? platformError.message : 'Unknown error',
            });
          }
        }

        // Check if any campaigns were scheduled successfully
        const successCount = results.filter(r => r.success).length;

        if (successCount === 0 && platforms.length > 0) {
          yield put(scheduleCampaignFailure('Failed to schedule campaign on any platform'));
        } else {
          yield put(scheduleCampaignSuccess({
            results,
            successCount,
            totalCount: platforms.length,
          }));
        }
      } catch (error) {
        console.error('Error scheduling campaign (new format):', error);
        throw error;
      }
      return;
    }

    // Old format: use platform_content_map
    const platforms = Object.keys(platform_content_map || {});

    // If no platforms are selected, make a single API call with empty social_media array
    if (platforms.length === 0) {
      try {
        const apiUrl = `${ENDPOINTS.schedule_campaign}/${restaurant_id}`;

        const requestBody: any = {
          start_date,
          end_date,
          social_media: [], // Empty array for no platforms
        };

        // Include generated_content_id if provided
        if (generated_content_id) {
          requestBody.generated_content_id = generated_content_id;
        }

        if (flyer_id) {
          requestBody.flyer_id = flyer_id;
        }

        const response: { data: any } = yield call(
          axios.post,
          apiUrl,
          requestBody
        );

        yield put(scheduleCampaignSuccess({
          results: [],
          successCount: 1,
          totalCount: 0,
          data: response.data,
        }));
      } catch (error) {
        console.error('Error scheduling campaign with no platforms:', error);
        throw error;
      }
      return;
    }

    // Schedule campaign for each platform with its specific content ID
    const results: any[] = [];

    for (const platform of platforms) {
      const contentId = platform_content_map![platform];

      try {
        const result: any = yield call(
          scheduleCampaignForPlatform,
          platform,
          contentId,
          start_date,
          end_date,
          restaurant_id,
          flyer_id
        );

        results.push({
          platform,
          success: true,
          data: result,
        });

      } catch (platformError) {
        console.error(`Error scheduling campaign for ${platform}:`, platformError);

        results.push({
          platform,
          success: false,
          error: platformError instanceof Error ? platformError.message : 'Unknown error',
        });
      }
    }

    // Check if any campaigns were scheduled successfully
    const successCount = results.filter(r => r.success).length;

    if (successCount === 0) {
      yield put(scheduleCampaignFailure('Failed to schedule campaign on any platform'));
    } else {
      yield put(scheduleCampaignSuccess({
        results,
        successCount,
        totalCount: platforms.length,
      }));
    }
  } catch (error) {
    console.error('Schedule campaign error:', error);

    let errorMessage = 'Failed to schedule campaign. Please try again.';

    if (error instanceof Error) {
      errorMessage = error.message;
    } else if (typeof error === 'string') {
      errorMessage = error;
    }

    yield put(scheduleCampaignFailure(errorMessage));
  }
}

// API call to fetch scheduled campaigns
function* fetchScheduledCampaignsApi(restaurant_id: string): Generator<any, any, any> {
  const apiUrl = `${ENDPOINTS.schedule_campaign}/${restaurant_id}`;

  const response: { data: any } = yield call(
    axios.get,
    apiUrl
  );

  return response.data;
}

function* fetchScheduledCampaignsSaga(action: { type: string; payload: { restaurant_id: string } }): Generator<any, void, any> {
  try {
    const result: any = yield call(fetchScheduledCampaignsApi, action.payload.restaurant_id);
    yield put(fetchScheduledCampaignsSuccess(result));
  } catch (error) {
    console.error('Fetch scheduled campaigns error:', error);

    let errorMessage = 'Failed to fetch scheduled campaigns. Please try again.';

    if (error instanceof Error) {
      errorMessage = error.message;
    } else if (typeof error === 'string') {
      errorMessage = error;
    }

    yield put(fetchScheduledCampaignsFailure(errorMessage));
  }
}

// API call to update campaign
function* updateCampaignApi(payload: UpdateCampaignRequest): Generator<any, any, any> {
  const { restaurant_id, campaign_id, ...requestBody } = payload;

  // API endpoint for updating campaign - restaurant_id only in URL, not in body
  const apiUrl = `${ENDPOINTS.schedule_campaign}/${restaurant_id}?campaignId=${campaign_id}`;

  // requestBody now contains: start_date, end_date, generated_content_id, social_media, flyer_id (if present)
  const response: { data: any } = yield call(
    axios.put,
    apiUrl,
    requestBody
  );

  return response.data;
}

function* updateCampaignSaga(action: { type: string; payload: UpdateCampaignRequest }): Generator<any, void, any> {
  try {
    const result: any = yield call(updateCampaignApi, action.payload);
    yield put(updateCampaignSuccess(result));
    // After successful update, fetch the updated campaigns list
    yield put(fetchScheduledCampaignsRequest({ restaurant_id: action.payload.restaurant_id }));
  } catch (error) {
    console.error('Update campaign error:', error);

    let errorMessage = 'Failed to update campaign. Please try again.';

    if (error instanceof Error) {
      errorMessage = error.message;
    } else if (typeof error === 'string') {
      errorMessage = error;
    }

    yield put(updateCampaignFailure(errorMessage));
  }
}

export function* watchCampaignPosting(): Generator<any, void, any> {
  yield takeEvery(postNowRequest.type, postNowSaga);
  yield takeEvery(scheduleCampaignRequest.type, scheduleCampaignSaga);
  yield takeEvery(fetchScheduledCampaignsRequest.type, fetchScheduledCampaignsSaga);
  yield takeEvery(updateCampaignRequest.type, updateCampaignSaga);
}
